import React from "react";
import { useSelector } from "react-redux";

import applePayLogo from "../../../assets/payment/applepay.svg";
import googlePayLogo from "../../../assets/payment/googlepay.svg";
import stripLogo from "../../../assets/strip.png";
import { useToast } from "../../../provider/ToastProvider";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForUsers } from "../../../utils/confighelpers";
import { defaultAvailablePaymentMethods } from "../../../utils/constants";
import { FieldError, FieldLabel, Icon, inputClass } from "../../classes/schedule/ui";
import { PrimaryButton, SecondaryButton, SettingsCard, Toggle } from "./shared";

/**
 * Online payments tab (replaces paymentProfile/BusinessPaymentProfile, Stripe only — Tikkie dropped).
 * Same requests as before:
 * - load: GET users/getUserInfo (myConnectedPaymentProfiles.stripe) + GET payments/getAvailablePaymentMethods (fees)
 * - connect / continue setup: POST payments/createConnectedAC { email, country, return_url } → go to Stripe
 * - save: PUT users/updateProfile { myConnectedPaymentProfiles: { stripe: { isEnabled, profileDetails } } }
 *   (the settings only show once a Stripe account is connected, which was always the "update" path before)
 * Bugs fixed: "at least one payment method" check now works; the page no longer stays on a spinner
 * when Stripe can't start the setup.
 */

const COUNTRIES = [
  { iso: "NL", name: "Netherlands" },
  { iso: "GB", name: "United Kingdom" },
  { iso: "US", name: "United States" },
  { iso: "IN", name: "India" },
];
const EMAIL_RE = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const OFF = { enable: false, chargeToCustomer: false };
// Official iDEAL | Wero logo, served by iDEAL (ideal.nl/en/ideal-wero-branding). Falls back to the name if it can't load.
const IDEAL_WERO_LOGO = "https://d1twnm33rljaon.cloudfront.net/Logos/iDEAL-Wero/iDEAL_Wero_Lockup_Yellow_Horizontal_RGB.svg";
const pm = (m) => (m && m.enable ? m : { ...OFF });

const feeText = (methods, key) => {
  const fee = methods?.[key]?.paymentGatewayServiceFee || {};
  const pct = fee.paymentProcessingPercentage > 0 ? `${fee.paymentProcessingPercentage}% + ` : "";
  return `${pct}€${Number(fee.paymentProcessingFlatFee || 0).toFixed(2).replace(".", ",")}`;
};

const toForm = (stripe) => ({
  isEnabled: !!stripe?.isEnabled,
  profileDetails: {
    ...(stripe?.profileDetails || {}),
    stripeAcId: stripe?.profileDetails?.stripeAcId || "",
    stripeEmail: stripe?.profileDetails?.stripeEmail || "",
    stripeCountry: stripe?.profileDetails?.stripeCountry || "",
    stripeOnBoardindStatus: stripe?.profileDetails?.stripeOnBoardindStatus || "",
    paymentMethod: {
      ...(stripe?.profileDetails?.paymentMethod || {}),
      ideal: pm(stripe?.profileDetails?.paymentMethod?.ideal),
      applePay: pm(stripe?.profileDetails?.paymentMethod?.applePay),
      googlePay: pm(stripe?.profileDetails?.paymentMethod?.googlePay),
      creditCard: pm(stripe?.profileDetails?.paymentMethod?.creditCard),
    },
  },
});

export default function PaymentsTab() {
  const { addSuccessToast, addErrorToast, addNotificationDialog } = useToast();
  const userReduxState = useSelector(userState);
  const user = userReduxState.userData || {};

  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState(() => toForm(null));
  const [base, setBase] = React.useState("");
  const [fees, setFees] = React.useState(defaultAvailablePaymentMethods);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
      if (res?.data?.statusCode === 200) {
        const f = toForm(res.data.data?.myConnectedPaymentProfiles?.stripe);
        setForm(f);
        setBase(JSON.stringify(f));
      }
    } catch (e) {
      /* keep defaults */
    }
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForBookings()}payments/getAvailablePaymentMethods?teacherID=${user._id}&paymentMethodsFor=event&bookingFor=event`,
      });
      if (res?.data?.statusCode === 200 && res.data.data) {
        setFees({ payAtVenue: { enable: false, processingFeeApplicable: true, reserveNowPaylater: true, paymentGatewayServiceFee: { paymentProcessingFlatFee: 0, paymentProcessingPercentage: 0 } }, ...res.data.data });
      }
    } catch (e) {
      /* keep defaults */
    }
    setLoading(false);
  }, [user._id]);

  React.useEffect(() => {
    load();
  }, [load]);

  const d = form.profileDetails;
  const connected = !!d.stripeAcId && d.stripeOnBoardindStatus === "SUCCESS";
  const started = !!d.stripeAcId && !connected;
  const dirty = JSON.stringify(form) !== base;
  // Payments on/off as saved (the on/off switch is gone: Stripe turns payments on when onboarding finishes).
  const savedOn = !!form.isEnabled;

  const setMethod = (keys, enable) => {
    setForm((f) => {
      const paymentMethod = { ...f.profileDetails.paymentMethod };
      keys.forEach((k) => {
        paymentMethod[k] = { ...paymentMethod[k], enable };
      });
      return { ...f, profileDetails: { ...f.profileDetails, paymentMethod } };
    });
    setErrors({});
  };

  const save = async (overrides = {}) => {
    const next = { ...form, ...overrides };
    const m = next.profileDetails.paymentMethod;
    const e = {};
    if (next.isEnabled && !m.ideal?.enable && !m.applePay?.enable && !m.googlePay?.enable && !m.creditCard?.enable) {
      e.paymentMethod = "Turn on at least one payment method.";
    }
    if (!next.profileDetails.stripeAcId) e.stripeAcId = "Please connect your Stripe account first.";
    setErrors(e);
    if (Object.keys(e).length) return;
    const body = { myConnectedPaymentProfiles: { stripe: { isEnabled: next.isEnabled, profileDetails: next.profileDetails } } };
    setSaving(true);
    try {
      const res = await axiosInstance({ method: "put", url: `${getUrlForUsers()}updateProfile`, data: body });
      if (res?.data?.statusCode === 200) {
        setForm(next);
        setBase(JSON.stringify(next));
        addSuccessToast(overrides.isEnabled ? "Online payments are on" : "Stripe settings saved");
      } else {
        addErrorToast(res?.data?.statusCode === 403 ? "Something is wrong please try again" : res?.data?.message || "Couldn't save.");
      }
    } catch (err) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SettingsCard>
        <div className="flex animate-pulse items-center gap-4">
          <span className="h-12 w-12 rounded-2xl bg-[#EDE5F0]" />
          <span className="flex-1">
            <span className="block h-4 w-40 rounded-full bg-[#EDE5F0]" />
            <span className="mt-2 block h-3 w-2/3 rounded-full bg-[#F3EEF5]" />
          </span>
        </div>
      </SettingsCard>
    );
  }

  const header = (pill, aside = null) => (
    <div className="flex flex-wrap items-start gap-4">
      <img src={stripLogo} alt="Stripe" className="h-12 w-12 shrink-0 rounded-2xl object-contain" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="text-lg font-black tracking-tight text-gray-900">Stripe</h2>
          {pill}
        </div>
        <p className="mt-1 text-sm text-gray-600">Members pay for passes and subscriptions online. Money goes straight to your own Stripe account.</p>
      </div>
      {aside}
    </div>
  );

  if (!connected) {
    return (
      <SettingsCard>
        {header(
          started ? (
            <span className="rounded-full bg-[#FFF4E5] px-2.5 py-0.5 text-xs font-extrabold text-[#7A4A00]">Setup not finished</span>
          ) : (
            <span className="rounded-full bg-[#F1EEF2] px-2.5 py-0.5 text-xs font-extrabold text-[#595159]">Not connected</span>
          )
        )}
        <ConnectStripe
          started={started}
          defaultEmail={d.stripeEmail || user?.myBusinessProfile?.workEmail || ""}
          defaultCountry={d.stripeCountry && COUNTRIES.some((c) => c.iso === d.stripeCountry) ? d.stripeCountry : "NL"}
          onFailed={(message) =>
            addNotificationDialog({
              dialogType: "error",
              dialogTitle: "An Error Occurred",
              dialogMessage: message || "Failed to create Stripe account: Could not verify provided information. Please check your details and try again.",
              dialogBtnTitle: "Go back",
            })
          }
        />
      </SettingsCard>
    );
  }

  const m = d.paymentMethod;
  const methods = [
    { keys: ["ideal"], name: "iDEAL | Wero", logo: "ideal", fee: feeText(fees, "ideal"), on: !!m.ideal?.enable },
    { keys: ["applePay", "googlePay"], name: "Apple Pay / Google Pay", logo: "wallets", fee: feeText(fees, "applePay"), on: !!m.applePay?.enable },
    { keys: ["creditCard"], name: "Credit card", logo: "card", fee: feeText(fees, "creditCard"), on: !!m.creditCard?.enable },
  ];

  return (
    <SettingsCard
      footer={
        <>
          {dirty && (
            <button
              type="button"
              onClick={() => {
                setForm(JSON.parse(base));
                setErrors({});
              }}
              className="rounded-xl px-3 py-2 text-sm font-extrabold text-gray-600 hover:bg-gray-50"
            >
              Discard changes
            </button>
          )}
          <PrimaryButton busy={saving} disabled={!dirty} onClick={() => save()}>
            Save
          </PrimaryButton>
        </>
      }
    >
      {header(
        savedOn ? (
          <span className="flex items-center gap-1.5 rounded-full bg-[#E8F7EE] px-2.5 py-0.5 text-xs font-extrabold text-[#1F6B3A]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#1F8F4E]" />
            Connected
          </span>
        ) : (
          <span className="rounded-full bg-[#FFF4E5] px-2.5 py-0.5 text-xs font-extrabold text-[#7A4A00]">Connected · payments off</span>
        ),
        null
      )}

      {!savedOn && (
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl bg-[#FFF8EC] px-4 py-3">
          <span className="min-w-0 flex-1 text-sm text-[#7A4A00]">Online payments are switched off, so members can't pay online right now.</span>
          <SecondaryButton busy={saving} onClick={() => save({ isEnabled: true })}>
            Turn on online payments
          </SecondaryButton>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          ["Stripe account", d.stripeAcId],
          ["Email", d.stripeEmail],
          ["Country", COUNTRIES.find((c) => c.iso === d.stripeCountry)?.name || d.stripeCountry],
        ].map(([label, value]) => (
          <div key={label} className="min-w-0 rounded-2xl bg-[#FAF8FB] px-4 py-3">
            <div className="text-xs font-extrabold text-gray-600">{label}</div>
            <div className="mt-0.5 truncate text-sm font-extrabold text-gray-900" title={value || ""}>
              {value || "–"}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-2 text-xs text-gray-500">To change or disconnect this Stripe account, contact FitClub support.</p>

      <div className="mt-6 text-sm font-black text-gray-900">Payment methods members can use</div>
      <div className={savedOn ? "" : "opacity-60"}>
        {methods.map((x) => (
          <div key={x.name} className="flex items-center gap-4 border-t border-gray-100 py-3.5 first:border-t-0">
            <span className="flex h-11 w-[92px] shrink-0 items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white px-1.5">
              <MethodLogo kind={x.logo} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-extrabold text-gray-900">{x.name}</div>
              <div className="text-xs text-gray-600">Processing fee: {x.fee}</div>
            </div>
            <Toggle label={x.name} checked={x.on} disabled={!savedOn} onChange={(v) => setMethod(x.keys, v)} />
          </div>
        ))}
      </div>
      {errors.paymentMethod ? <FieldError>{errors.paymentMethod}</FieldError> : errors.stripeAcId ? <FieldError>{errors.stripeAcId}</FieldError> : <p className="mt-1 text-xs text-gray-500">{savedOn ? "Keep at least one method on." : "Turn on online payments to choose payment methods."}</p>}
    </SettingsCard>
  );
}

/** Email + country → POST payments/createConnectedAC → continue on Stripe (same tab, as before). */
function ConnectStripe({ started, defaultEmail, defaultCountry, onFailed }) {
  const [email, setEmail] = React.useState(defaultEmail);
  const [country, setCountry] = React.useState(defaultCountry);
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);

  const go = async () => {
    const e = {};
    if (!email) e.email = "Please enter an email.";
    else if (!EMAIL_RE.test(email)) e.email = "Please enter a valid email.";
    if (!country) e.country = "Please pick a country.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}payments/createConnectedAC`,
        data: { email, country, return_url: `${window.location.origin}/my-account/online-payment` },
      });
      const url = res?.data?.statusCode === 200 ? res.data?.data?.on_boarding_url : "";
      if (url) {
        window.location = url;
        return; // leaving the page
      }
      onFailed(res?.data?.statusCode === 200 ? res?.data?.message : typeof res?.data?.message === "string" ? res.data.message : "");
    } catch (err) {
      onFailed("");
    }
    setBusy(false);
  };

  return (
    <>
      {started && <p className="mt-4 rounded-2xl bg-[#FFF8EC] px-4 py-3 text-sm text-[#7A4A00]">Stripe still needs a few details before you can take payments. Continue where you left off.</p>}
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="stripe-email">Email for your Stripe account</FieldLabel>
          <input
            id="stripe-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((x) => ({ ...x, email: "" }));
            }}
            className={inputClass(errors.email)}
          />
          <FieldError>{errors.email}</FieldError>
        </div>
        <div>
          <FieldLabel htmlFor="stripe-country">Country</FieldLabel>
          <select id="stripe-country" value={country} onChange={(e) => setCountry(e.target.value)} className={inputClass(errors.country)}>
            {COUNTRIES.map((c) => (
              <option key={c.iso} value={c.iso}>
                {c.name}
              </option>
            ))}
          </select>
          <FieldError>{errors.country}</FieldError>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-5">
        <span className="text-sm text-gray-600">You'll finish the setup on Stripe and come straight back here.</span>
        <PrimaryButton busy={busy} onClick={go}>
          {started ? "Continue setup" : "Add Stripe account"}
          <Icon className="h-4 w-4" d={<path d="M7 17 17 7M8 7h9v9" />} />
        </PrimaryButton>
      </div>
    </>
  );
}

/** Logo box content for each payment method. */
function MethodLogo({ kind }) {
  const [failed, setFailed] = React.useState(false);
  if (kind === "ideal") {
    return failed ? (
      <span className="text-[11px] font-black leading-tight text-gray-900">iDEAL | Wero</span>
    ) : (
      <img src={IDEAL_WERO_LOGO} alt="iDEAL | Wero" onError={() => setFailed(true)} className="h-7 w-full object-contain" />
    );
  }
  if (kind === "wallets") {
    return (
      <>
        <img src={applePayLogo} alt="Apple Pay" className="h-9 w-9 object-contain" />
        <img src={googlePayLogo} alt="Google Pay" className="h-9 w-9 object-contain" />
      </>
    );
  }
  // Standard credit card icon (Heroicons credit-card).
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7 text-brown-coffee" aria-label="Credit card" role="img">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
    </svg>
  );
}
